import React, { useContext, useState, useEffect, useCallback } from 'react';
import { auth, db } from '../../firebase';

import {
	createUserWithEmailAndPassword,
	signInWithEmailAndPassword,
	signOut,
	sendPasswordResetEmail,
	onAuthStateChanged,
	updatePassword,
	GoogleAuthProvider,
	signInWithPopup,
	signInWithRedirect,
	getRedirectResult,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { disablePushNotifications } from '../../services/pushNotifications';

const AuthContext = React.createContext();

export function useAuth() {
	return useContext(AuthContext);
}

// Popups are unreliable on mobile Safari and in installed (standalone) PWAs,
// which is how teachers open the portal — use a full-page redirect there and
// keep the popup on desktop.
function shouldUseGoogleRedirect() {
	if (typeof window === 'undefined') return false;
	const nav = window.navigator || {};
	const standalone =
		window.matchMedia?.('(display-mode: standalone)')?.matches ||
		nav.standalone === true;
	const mobile = /Android|iPhone|iPad|iPod|Mobile/i.test(nav.userAgent || '');
	return standalone || mobile;
}

// Self-provisioning is GONE (fall model §3g, 2026-07-15): a sign-in never
// writes a users doc from the client — the flipped rules deny it anyway.
// Accounts are enrollment-gated: the users doc is created server-side by the
// claimPortalAccount Cloud Function when a family completes their emailed
// claim link (/portal/claim), and staff docs are provisioned by an admin. A
// signed-in user with NO users doc is "unlinked" — surfaces route them to the
// invite explainer instead of a portal.

export function AuthProvider({ children }) {
	const [currentUser, setCurrentUser] = useState();
	const [userRole, setUserRole] = useState(null);
	const [userProfile, setUserProfile] = useState(null);
	const [loading, setLoading] = useState(true);
	// The rejection from a redirect-based Google sign-in, held so the page the
	// browser lands back on can show it. Null on the popup path, which catches
	// its own error inline and never leaves the page.
	const [redirectError, setRedirectError] = useState(null);

	// Auth-account creation ONLY — no Firestore write. Used by the claim page:
	// the users doc (role, familyId binding) is created server-side by
	// claimPortalAccount right after this, never from the client.
	function createAuthAccount(email, password) {
		return createUserWithEmailAndPassword(auth, email, password);
	}

	async function getUserProfile(uid) {
		const userDoc = await getDoc(doc(db, 'users', uid));
		if (userDoc.exists()) {
			return userDoc.data();
		}
		return null;
	}

	function login(email, password) {
		return signInWithEmailAndPassword(auth, email, password);
	}

	// Re-read the users doc for the current session and update role/profile
	// state. The claim page calls this after claimPortalAccount creates the
	// users doc server-side — the auth-state listener ran before that doc
	// existed, so routing would otherwise still see the session as unlinked.
	async function refreshProfile() {
		const user = auth.currentUser;
		if (!user) return null;
		const profile = await getUserProfile(user.uid);
		setUserProfile(profile);
		setUserRole(profile?.role || null);
		return profile;
	}

	async function signInWithGoogle() {
		const provider = new GoogleAuthProvider();
		// Always show the account chooser, even when one Google account is
		// already signed in on the device.
		provider.setCustomParameters({ prompt: 'select_account' });

		if (shouldUseGoogleRedirect()) {
			// Full-page round-trip: the result is consumed by
			// getRedirectResult() on load (see the effect below). Returns
			// after navigating away, so there is no credential to hand back.
			await signInWithRedirect(auth, provider);
			return null;
		}

		const userCredential = await signInWithPopup(auth, provider);
		return userCredential;
	}

	async function logout() {
		// Best-effort: delete THIS device's push token while auth is still valid,
		// so on a shared device the next person to sign in isn't reachable by the
		// previous user's task pushes. Must run BEFORE signOut (the pushTokens
		// self-delete rule depends on request.auth matching) and must never block
		// or slow-fail sign-out — a token that can't be cleared is not a reason to
		// keep the user signed in.
		try {
			await disablePushNotifications();
		} catch (e) {
			/* ignore — never block logout on token cleanup */
		}
		setUserRole(null);
		setUserProfile(null);
		return signOut(auth);
	}

	function resetPassword(email) {
		return sendPasswordResetEmail(auth, email);
	}

	function updateEmail(email) {
		return updateEmail(currentUser.updateEmail, email);
	}

	function updatePasswordFunc(password) {
		return updatePassword(currentUser, password);
	}

	const clearRedirectError = useCallback(() => setRedirectError(null), []);

	useEffect(() => {
		// Surface any error from a redirect-based Google sign-in.
		//
		// THIS USED TO BE console.error ALONE, AND THAT MADE THE MOBILE PATH
		// FAIL SILENTLY. `shouldUseGoogleRedirect()` sends every phone and every
		// installed PWA down the redirect path, so the one path with no visible
		// error handling was the one most families use. The rejection is now
		// held in state for the page to render; the console line stays, because
		// the code is still the fastest thing to read in a live diagnosis.
		//
		// The ERROR OBJECT is stored, not a message: the two consumers give
		// different advice on failure (log in with a password vs set one), and
		// that choice belongs to the page that knows what else is on screen.
		// See googleSignInError.js.
		getRedirectResult(auth).catch((error) => {
			console.error('Google redirect sign-in error:', error);
			setRedirectError(error);
		});

		const unsubscribe = onAuthStateChanged(auth, async (user) => {
			setCurrentUser(user);
			if (user) {
				try {
					// No auto-provisioning on a missing profile (§3g flip): a
					// sign-in with no users doc stays role-null ("unlinked") and
					// is routed to the invite explainer by RoleBasedRoute /
					// PortalLogin — never silently granted a role. The claim page
					// re-reads the profile itself after claimPortalAccount runs.
					const profile = await getUserProfile(user.uid);
					setUserProfile(profile);
					setUserRole(profile?.role || null);
				} catch (error) {
					// A failed profile read must NOT mint a fake role (the old
					// fallback faked 'customer', which post-flip would render a
					// portal with no backing doc). Null role → unlinked notice.
					console.error('Error fetching user profile:', error);
					setUserProfile(null);
					setUserRole(null);
				}
			} else {
				setUserProfile(null);
				setUserRole(null);
			}
			setLoading(false);
		});

		return unsubscribe;
	}, []);

	const value = {
		currentUser,
		userRole,
		userProfile,
		login,
		createAuthAccount,
		signInWithGoogle,
		refreshProfile,
		logout,
		resetPassword,
		updateEmail,
		updatePassword: updatePasswordFunc,
		getUserProfile,
		redirectError,
		// Consumed once and cleared, so a failure from an earlier attempt cannot
		// reappear on a later visit to the same page.
		clearRedirectError,
	};

	return (
		<AuthContext.Provider value={value}>
			{!loading && children}
		</AuthContext.Provider>
	);
}
